import React, { useRef, useState, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Typography, Button, Alert } from '@mui/material';
import { MyLocation } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createMapInstance, createPinMarkerElement } from '../../utils/maplibreConfig';

const DEFAULT_POSITION = [48.8566, 2.3522]; // Paris [lat, lng]
const DEFAULT_ZOOM = 13;

export default function ListingMap({ 
  initialPosition = DEFAULT_POSITION, 
  initialZoom = DEFAULT_ZOOM,
  onPositionChange = null,
  height = 400
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Valider et normaliser la position initiale [lat, lng]
  const validInitialPosition = Array.isArray(initialPosition) && 
    typeof initialPosition[0] === 'number' && 
    typeof initialPosition[1] === 'number' 
    ? initialPosition 
    : DEFAULT_POSITION;

  const [position, setPosition] = useState(validInitialPosition);
  const [zoom, setZoom] = useState(initialZoom);

  // Initialisation de la carte (une seule fois)
  useEffect(() => {
    if (!containerRef.current || mapInstanceRef.current) return;

    const [lat, lng] = validInitialPosition;

    const map = createMapInstance({
      container: containerRef.current,
      center: [lng, lat],
      zoom: initialZoom,
    });

    // Marqueur draggable
    const marker = new maplibregl.Marker({
      draggable: true,
      element: createPinMarkerElement({
        accentColor: '#ea580c',
        innerColor: '#fff7ed',
        size: 22,
        shadowColor: 'rgba(234,88,12,0.24)',
      }),
      anchor: 'center',
    })
      .setLngLat([lng, lat])
      .addTo(map);

    marker.on('dragend', () => {
      const lngLat = marker.getLngLat();
      const newPos = [lngLat.lat, lngLat.lng];
      setPosition(newPos);
      if (onPositionChange) {
        onPositionChange({
          latitude: lngLat.lat,
          longitude: lngLat.lng,
          zoom: map.getZoom(),
        });
      }
    });

    // Clic sur la carte pour repositionner le marqueur
    map.on('click', (e) => {
      const { lat, lng } = e.lngLat;
      const newPos = [lat, lng];
      marker.setLngLat([lng, lat]);
      setPosition(newPos);
      map.flyTo({ center: [lng, lat], zoom: map.getZoom() });
      if (onPositionChange) {
        onPositionChange({ latitude: lat, longitude: lng, zoom: map.getZoom() });
      }
    });

    map.on('zoomend', () => {
      setZoom(Math.round(map.getZoom()));
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mise à jour si initialPosition change depuis l'extérieur
  useEffect(() => {
    if (!Array.isArray(initialPosition) ||
        typeof initialPosition[0] !== 'number' ||
        typeof initialPosition[1] !== 'number') return;

    const [lat, lng] = initialPosition;
    setPosition(initialPosition);

    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo({ center: [lng, lat], zoom: mapInstanceRef.current.getZoom() });
    }
    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
    }
  }, [initialPosition]);

  // Géolocalisation
  const getUserLocation = () => {
    if (!('geolocation' in navigator)) {
      alert('La géolocalisation n\'est pas supportée par votre navigateur.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userPos = [pos.coords.latitude, pos.coords.longitude];
        setPosition(userPos);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 15 });
        }
        if (markerRef.current) {
          markerRef.current.setLngLat([pos.coords.longitude, pos.coords.latitude]);
        }
        if (onPositionChange) {
          onPositionChange({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, zoom: 15 });
        }
      },
      (error) => {
        console.error('Erreur de géolocalisation:', error);
        alert('Impossible d\'obtenir votre position actuelle. ' + error.message);
      }
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<MyLocation />}
          onClick={getUserLocation}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
        />
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          {t('listing.address.coordinatesTip')}
        </Typography>
      </Box>

      <Box
        sx={{
          height: height,
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      </Box>

      <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '8px', fontSize: '0.85rem' }}>
        <Typography variant="body2">
          <strong>Latitude:</strong> {typeof position[0] === 'number' ? position[0].toFixed(5) : 'N/A'} |{' '}
          <strong>Longitude:</strong> {typeof position[1] === 'number' ? position[1].toFixed(5) : 'N/A'} |{' '}
          <strong>Zoom:</strong> {zoom}
        </Typography>
      </Box>
    </Box>
  );
}
